import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-map-gl';
import debounce from '../../search/utils';
import { bboxPolygon } from '@turf/bbox-polygon';
import { booleanWithin } from '@turf/boolean-within';
import { pointsWithinPolygon } from '@turf/points-within-polygon';

const useMapHelpers = () => {
  const { locale } = useRouter();
  const [zoom, setZoom] = useState<number | undefined>(undefined);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const drawerOpen = useSelector((state: any) => state.map.drawerOpen);
  const compounds = useSelector((state: any) => state.map.compounds);
  const { current: mapRef } = useMap();
  const isViewBoundsWithinBounds = (viewBounds: any, bounds: any) => {
    const viewBoundsPolygon = bboxPolygon([
      viewBounds.bottomRight.long,
      viewBounds.bottomRight.lat,
      viewBounds.topLeft.long,
      viewBounds.topLeft.lat
    ]);
    const boundsPolygon = bboxPolygon([
      bounds.bottomRight.long,
      bounds.bottomRight.lat,
      bounds.topLeft.long,
      bounds.topLeft.lat
    ]);
    return booleanWithin(viewBoundsPolygon, boundsPolygon);
  };

  const isPointWithinBounds = (points: any) => {
    const bounds = mapRef?.getBounds();
    if (!bounds) return false;
    const boundsPolygon = bboxPolygon([
      bounds._ne.lng,
      bounds._ne.lat,
      bounds._sw.lng,
      bounds._sw.lat
    ]);
    
    return pointsWithinPolygon(points, boundsPolygon).features;
  };

  const getBoundsPadding = () => {
    let padding = {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    };

    if (isMobile) {
      return {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50
      };
    } else {
      padding.top = 10;
      padding.bottom = 25;

      if (drawerOpen) {
        padding.left = locale === 'en' ? 100 : 500;
        padding.right = locale === 'ar' ? 100 : 500;
      }
    }

    return padding;
  };

  const getCompoundBounds = () => {
    if (compounds) {
      const firstCompound = compounds[0].location_point;
      const lastCompound = compounds[compounds.length - 1].location_point;

      return { firstCompound, lastCompound };
    }

    return {};
  };

  const shouldShowCompound = (index: number): boolean => {
    if (compounds?.length < 8) {
      return true;
    }

    if (zoom === undefined) return true;

    if (isMobile) {
      if (zoom < 4) return false;
      if (zoom >= 12) return true;
      if (zoom <= 6) return index % 15 === 0;
      if (zoom <= 9) return index % 8 === 0;
      return index % 3 === 0;
    } else {
      if (zoom <= 8) return false;
      if (zoom < 10) return index % 10 === 0;
      if (zoom < 12) return index % 4 === 0;
      return true;
    }
  };

  const handleZoom = useCallback(
    debounce(() => {
      if (mapRef) {
        setZoom(mapRef.getZoom());
      }
    }, 200),
    [mapRef]
  );
  useEffect(() => {
    if (mapRef) {
      mapRef.on('zoom', handleZoom);
      return () => {
        mapRef.off('zoom', handleZoom);
      };
    }
  }, [mapRef, handleZoom]);
  return {
    getBoundsPadding,
    getCompoundBounds,
    shouldShowCompound,
    zoom,
    isViewBoundsWithinBounds,
    isPointWithinBounds
  };
};

export default useMapHelpers;
